<template>
  <b-modal
    id="allocate-chassis-modal"
    ref="modal"
    size="lg"
    centered
    hide-header
    hide-footer
    body-class="ac-modal-body"
    @show="onShow"
    @hidden="onHidden"
  >
    <!-- ===== HEADER ===== -->
    <header class="ac-header">
      <div class="ac-header__title-row">
        <button
          v-if="viewMode === 'chassis'"
          class="ac-back-btn"
          @click="backToList"
          aria-label="Quay lại"
        >
          <i class="fa-solid fa-arrow-left"></i>
        </button>
        <div v-else class="ac-header__icon">
          <i class="fa-solid fa-car-side"></i>
        </div>

        <div class="ac-header__title">
          <h4 v-if="viewMode === 'list'">Phân bổ số khung &mdash; số máy</h4>
          <h4 v-else>
            Số khung số máy cho
            <span class="ac-header__product">{{
              activeRow ? activeRow.productName : ''
            }}</span>
          </h4>

          <p v-if="contract" class="ac-header__meta">
            <span class="ac-tag">
              <i class="fa-solid fa-hashtag"></i>
              {{ contract.contractNumber || contract.id.slice(0, 8) }}
            </span>
            <span class="ac-tag">
              <i class="fa-solid fa-store"></i>
              {{ contract.store ? contract.store.name : '—' }}
            </span>
            <span class="ac-tag" v-if="viewMode === 'list'">
              <i class="fa-solid fa-user"></i>
              {{ contract.customerName }}
            </span>
          </p>
        </div>
        <button class="ac-close-btn" @click="hide" aria-label="Đóng">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <!-- Progress bar — chỉ hiện ở view list -->
      <div
        v-if="viewMode === 'list' && !loading && chassisRows.length"
        class="ac-progress"
      >
        <div class="ac-progress__bar">
          <div
            class="ac-progress__fill"
            :style="{ width: progressPct + '%' }"
          ></div>
        </div>
        <div class="ac-progress__label">
          <span class="ac-progress__num">{{ allocatedCount }}</span>
          <span class="ac-progress__divider">/</span>
          <span>{{ chassisRows.length }} xe đã phân bổ</span>
        </div>
      </div>
    </header>

    <!-- ===== BODY ===== -->
    <section class="ac-body">
      <!-- LOADING -->
      <div v-if="loading" class="ac-state ac-state--loading">
        <b-spinner variant="primary" />
        <span>Đang tải thông tin hợp đồng...</span>
      </div>

      <!-- EMPTY (chỉ ở list mode) -->
      <div
        v-else-if="viewMode === 'list' && !chassisRows.length"
        class="ac-state ac-state--empty"
      >
        <div class="ac-empty-icon">
          <i class="fa-regular fa-circle-check"></i>
        </div>
        <h5>Không có xe cần phân bổ</h5>
        <p>HĐ không chứa sản phẩm CHASSIS_ENGINE.</p>
      </div>

      <!-- ============================ -->
      <!-- VIEW 1: VEHICLE CARD LIST    -->
      <!-- ============================ -->
      <ul v-else-if="viewMode === 'list'" class="ac-vehicle-list">
        <li
          v-for="(row, idx) in chassisRows"
          :key="row.contractDetailId"
          :style="{ '--i': idx }"
          class="ac-vehicle-card"
          :class="{
            'is-allocated': !!row.currentChassisNumber,
            'is-picked':
              !row.currentChassisNumber &&
              !!selectedChassisMap[row.contractDetailId],
          }"
        >
          <div class="ac-vehicle-card__index">
            {{ String(idx + 1).padStart(2, '0') }}
          </div>

          <div class="ac-vehicle-card__main">
            <div class="ac-vehicle-card__name">
              {{ row.productName }}
            </div>
            <div v-if="row.productCode" class="ac-vehicle-card__code">
              {{ row.productCode }}
            </div>

            <div class="ac-vehicle-card__status">
              <template v-if="row.currentChassisNumber">
                <span class="ac-status-pill ac-status-pill--success">
                  <i class="fa-solid fa-check"></i>
                  Đã phân bổ
                </span>
                <span class="ac-chassis-snapshot">
                  <span class="ac-chassis-snapshot__label">SK</span>
                  {{ row.currentChassisNumber }}
                  <span class="ac-chassis-snapshot__sep"></span>
                  <span class="ac-chassis-snapshot__label">SM</span>
                  {{ row.currentEngineNumber }}
                </span>
              </template>
              <template v-else-if="selectedChassisMap[row.contractDetailId]">
                <span class="ac-status-pill ac-status-pill--picked">
                  <i class="fa-solid fa-circle-dot"></i>
                  Đã chọn (chưa lưu)
                </span>
                <span class="ac-chassis-snapshot ac-chassis-snapshot--pending">
                  <span class="ac-chassis-snapshot__label">SK</span>
                  {{
                    selectedChassisLabels[row.contractDetailId].chassisNumber
                  }}
                  <span class="ac-chassis-snapshot__sep"></span>
                  <span class="ac-chassis-snapshot__label">SM</span>
                  {{ selectedChassisLabels[row.contractDetailId].engineNumber }}
                </span>
              </template>
              <template v-else>
                <span class="ac-status-pill ac-status-pill--pending">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  Chưa phân bổ
                </span>
              </template>
            </div>
          </div>

          <!-- Action: nút mở chassis view, hoặc icon locked -->
          <div class="ac-vehicle-card__action">
            <div
              v-if="row.currentChassisNumber"
              class="ac-locked"
              title="Chassis đã gán — không cho đổi qua modal này"
            >
              <i class="fa-solid fa-lock"></i>
            </div>
            <button
              v-else
              class="ac-pick-btn"
              :class="{
                'is-picked': !!selectedChassisMap[row.contractDetailId],
              }"
              @click="openChassisView(row)"
            >
              <i
                class="fa-solid"
                :class="
                  selectedChassisMap[row.contractDetailId]
                    ? 'fa-pen-to-square'
                    : 'fa-plus'
                "
              ></i>
              {{ selectedChassisMap[row.contractDetailId] ? 'Đổi' : 'Chọn' }}
            </button>
          </div>
        </li>
      </ul>

      <!-- ============================ -->
      <!-- VIEW 2: CHASSIS TABLE        -->
      <!-- ============================ -->
      <div v-else-if="viewMode === 'chassis'" class="ac-chassis-view">
        <div v-if="loadingChassis" class="ac-state ac-state--loading">
          <b-spinner small variant="primary" />
          <span>Đang tải danh sách số khung — số máy...</span>
        </div>

        <div
          v-else-if="!availableChassis.length"
          class="ac-state ac-state--empty"
        >
          <div class="ac-empty-icon ac-empty-icon--warning">
            <i class="fa-solid fa-inbox"></i>
          </div>
          <h5>Không có xe để ghép</h5>
          <p>
            Cửa hàng
            <b>{{ contract && contract.store ? contract.store.name : '' }}</b>
            hiện không có xe NEW trong kho, cũng không có xe đang đặt (PO) cho
            sản phẩm này. Cần nhập kho hoặc tạo Đơn đặt xe.
          </p>
        </div>

        <div v-else class="ac-chassis-table-wrap">
          <table class="ac-chassis-table">
            <thead>
              <tr>
                <th class="ac-col-product">Sản phẩm</th>
                <th class="ac-col-chassis">Số khung</th>
                <th class="ac-col-engine">Số máy</th>
                <th class="ac-col-price">Giá bán</th>
                <th class="ac-col-status">Trạng thái</th>
                <th class="ac-col-action"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(c, idx) in availableChassis"
                :key="c.id"
                :style="{ '--i': idx }"
                class="ac-chassis-row"
              >
                <td class="ac-col-product">{{ activeRow.productName }}</td>
                <td class="ac-col-chassis">
                  <span class="ac-mono">{{ c.chassisNumber || '—' }}</span>
                </td>
                <td class="ac-col-engine">
                  <span class="ac-mono">{{
                    engineDisplay(c.engineNumber)
                  }}</span>
                </td>
                <td class="ac-col-price">{{ formatPrice(c.sellingPrice) }}</td>
                <td class="ac-col-status">
                  <span
                    v-if="c.status === 'PO_ORDERED'"
                    class="ac-status-pill ac-status-pill--pending"
                    >Đặt xe (chưa về)</span
                  >
                  <span v-else class="ac-status-pill ac-status-pill--new"
                    >Mới</span
                  >
                </td>
                <td class="ac-col-action">
                  <button class="ac-choose-btn" @click="pickChassis(c)">
                    Chọn
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="ac-chassis-footer">
            <span class="ac-total">Tổng số: {{ availableChassis.length }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== FOOTER ===== -->
    <footer v-if="viewMode === 'list'" class="ac-footer">
      <div class="ac-footer__hint">
        <i class="fa-solid fa-info-circle"></i>
        Phân bổ đủ chassis + HĐ ở "Đã đặt cọc" → tự duyệt giữ hàng.
      </div>
      <div class="ac-footer__actions">
        <button
          class="ac-btn ac-btn--ghost"
          @click="hide"
          :disabled="submitting"
        >
          Đóng
        </button>
        <button
          class="ac-btn ac-btn--primary"
          :disabled="submitting || !canSubmit"
          @click="onSubmit"
        >
          <b-spinner v-if="submitting" small class="mr-2" />
          <i v-else class="fa-solid fa-circle-check mr-2"></i>
          <span>
            Phân bổ
            <strong v-if="pickedCount">&nbsp;{{ pickedCount }} xe</strong>
          </span>
        </button>
      </div>
    </footer>
    <footer v-else class="ac-footer">
      <div class="ac-footer__hint">
        <i class="fa-solid fa-hand-pointer"></i>
        Bấm "Chọn" trên dòng để gán số khung — số máy cho xe.
      </div>
      <div class="ac-footer__actions">
        <button class="ac-btn ac-btn--ghost" @click="backToList">
          <i class="fa-solid fa-arrow-left mr-2"></i>
          Quay lại
        </button>
      </div>
    </footer>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import ContractService from '@/core/services/contract.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import { get } from 'lodash';

const PRODUCT_TYPE_CHASSIS_ENGINE = 'CHASSIS_ENGINE';

export default {
  name: 'AllocateChassisModal',
  props: {
    contractId: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      contract: null,
      chassisRows: [],
      // Map contractDetailId → productChassisId user vừa chọn (pending lưu).
      selectedChassisMap: {},
      // Map contractDetailId → {chassisNumber, engineNumber} hiển thị inline
      // sau khi pick (trước khi server save).
      selectedChassisLabels: {},

      // View switching
      viewMode: 'list', // 'list' | 'chassis'
      activeRow: null, // contractDetail row đang được phân bổ
      availableChassis: [], // chassis options từ products/chassis-engine
      loadingChassis: false,
    };
  },
  computed: {
    allocatedCount() {
      return this.chassisRows.filter(
        (r) =>
          !!r.currentChassisNumber ||
          !!this.selectedChassisMap[r.contractDetailId],
      ).length;
    },
    pickedCount() {
      return Object.keys(this.selectedChassisMap).length;
    },
    progressPct() {
      if (!this.chassisRows.length) return 0;
      return Math.round((this.allocatedCount / this.chassisRows.length) * 100);
    },
    // Xe ảo PO đã gán sẵn (từ form tạo/sửa HĐ) còn PO_ORDERED → cần bấm
    // "Duyệt giữ hàng" để flip sang giữ chỗ, dù user không pick lại trong modal.
    hasUnheldPoChassis() {
      return this.chassisRows.some(
        (r) => r.currentChassisStatus === 'PO_ORDERED',
      );
    },
    canSubmit() {
      return this.pickedCount > 0 || this.hasUnheldPoChassis;
    },
  },
  methods: {
    show() {
      this.$bvModal.show('allocate-chassis-modal');
    },
    hide() {
      this.$bvModal.hide('allocate-chassis-modal');
    },
    onShow() {
      this.selectedChassisMap = {};
      this.selectedChassisLabels = {};
      this.viewMode = 'list';
      this.activeRow = null;
      this.availableChassis = [];
      this.fetchContract();
    },
    onHidden() {
      this.contract = null;
      this.chassisRows = [];
      this.selectedChassisMap = {};
      this.selectedChassisLabels = {};
      this.viewMode = 'list';
      this.activeRow = null;
      this.availableChassis = [];
    },

    async fetchContract() {
      if (!this.contractId) return;
      this.loading = true;
      try {
        const res = await ContractService.getById(this.contractId);
        const data = get(res, 'data.data');
        if (!data) {
          makeToastFaile('Không tải được HĐ');
          return;
        }
        this.contract = data;
        // Defensive: BE response shape có thể đổi, hoặc contractDetail rỗng/null.
        // `Array.isArray` guard tránh `.filter is not a function` crash.
        const details = Array.isArray(data.contractDetail)
          ? data.contractDetail
          : [];
        // BE `findOne` (nestGiftsIntoContractDetails) gom dòng quà (PRODUCT_BONUS)
        // và bán kèm (PRODUCT_ATTACH) vào `gifts`/`attachs` của dòng cha → top-level
        // chỉ còn dòng chính. Quà/bán kèm VẪN có thể là xe CHASSIS_ENGINE (vd tặng
        // kèm VF3) cần phân bổ số khung — phải làm phẳng dòng con lại, nếu không
        // modal báo "không có xe" dù HĐ có xe ở dòng quà/bán kèm.
        const flatDetails = [];
        for (const d of details) {
          if (!d) continue;
          flatDetails.push(d);
          if (Array.isArray(d.gifts)) flatDetails.push(...d.gifts);
          if (Array.isArray(d.attachs)) flatDetails.push(...d.attachs);
        }
        this.chassisRows = flatDetails
          .filter(
            (d) =>
              d && d.product && d.product.type === PRODUCT_TYPE_CHASSIS_ENGINE,
          )
          .map((d) => ({
            contractDetailId: d.id,
            productId: d.productId,
            productName: d.productName || (d.product && d.product.name) || '—',
            productCode: (d.product && d.product.code) || '',
            currentChassisId: d.productChassisId,
            currentChassisNumber:
              (d.productChassis && d.productChassis.chassisNumber) || null,
            currentEngineNumber:
              (d.productChassis && d.productChassis.engineNumber) || null,
            currentChassisStatus:
              (d.productChassis && d.productChassis.status) || null,
          }));
      } catch (err) {
        makeToastFaile(get(err, 'response.data.message') || 'Lỗi tải HĐ');
      } finally {
        this.loading = false;
      }
    },

    /**
     * View 1 → View 2: load chassis list cho product của row được click.
     */
    async openChassisView(row) {
      this.activeRow = row;
      this.viewMode = 'chassis';
      this.availableChassis = [];
      if (!this.contract) return;
      this.loadingChassis = true;
      try {
        // BE `listProductChassisEngine` dùng TypeORM `IN (:...status)` → status
        // PHẢI là Array. 2 nguồn xe để ghép:
        //  (1) xe vật lý NEW trong KHO của HĐ (cùng store).
        //  (2) xe ảo PO_ORDERED (đang đặt, storeId=NULL) — "giữ chỗ" cho khách;
        //      KHÔNG lọc store (xe chưa về kho). allocate BE chấp nhận xe ảo.
        const parseList = (res) => {
          const raw =
            get(res, 'data.data.data') ||
            get(res, 'data.data.dataset') ||
            get(res, 'data.data') ||
            [];
          return Array.isArray(raw) ? raw : [];
        };
        const settled = await Promise.allSettled([
          ApiService.query('products/chassis-engine', {
            params: {
              productId: row.productId,
              storeId: this.contract.storeId,
              status: ['NEW'],
              limit: 50,
              page: 1,
            },
          }),
          ApiService.query('products/chassis-engine', {
            params: {
              productId: row.productId,
              status: ['PO_ORDERED'],
              limit: 50,
              page: 1,
            },
          }),
        ]);
        const newList =
          settled[0].status === 'fulfilled' ? parseList(settled[0].value) : [];
        const poList =
          settled[1].status === 'fulfilled' ? parseList(settled[1].value) : [];
        // Gộp + dedupe theo id (NEW trước, PO sau).
        const byId = new Map();
        [...newList, ...poList].forEach((c) => {
          if (c && c.id && !byId.has(c.id)) byId.set(c.id, c);
        });

        // Loại bỏ chassis đã được pick cho row khác trong cùng session
        // (cho phép chassis hiện đang được chọn cho activeRow này — user xem lại).
        const alreadyPicked = new Set(Object.values(this.selectedChassisMap));
        const currentPick = this.selectedChassisMap[row.contractDetailId];
        if (currentPick) alreadyPicked.delete(currentPick);

        this.availableChassis = [...byId.values()].filter(
          (c) => !alreadyPicked.has(c.id),
        );
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') || 'Lỗi tải danh sách chassis',
        );
      } finally {
        this.loadingChassis = false;
      }
    },

    /**
     * User click "Chọn" trên dòng chassis → gán cho activeRow + back về list.
     */
    pickChassis(chassis) {
      if (!this.activeRow) return;
      this.$set(
        this.selectedChassisMap,
        this.activeRow.contractDetailId,
        chassis.id,
      );
      this.$set(this.selectedChassisLabels, this.activeRow.contractDetailId, {
        chassisNumber: chassis.chassisNumber,
        engineNumber: chassis.engineNumber,
      });
      this.backToList();
    },

    backToList() {
      this.viewMode = 'list';
      this.activeRow = null;
      this.availableChassis = [];
    },

    async onSubmit() {
      if (!this.canSubmit) return;
      const items = Object.entries(this.selectedChassisMap).map(
        ([contractDetailId, productChassisId]) => ({
          contractDetailId,
          productChassisId,
        }),
      );
      // Xe ảo PO đã gán sẵn (chưa pick lại trong modal) còn PO_ORDERED → đưa vào
      // items để bước "Duyệt giữ hàng" flip sang giữ chỗ (BE cũng tự sweep,
      // đây là gửi tường minh để chắc chắn + thỏa ArrayMinSize của DTO).
      this.chassisRows.forEach((r) => {
        if (
          r.currentChassisStatus === 'PO_ORDERED' &&
          r.currentChassisId &&
          !this.selectedChassisMap[r.contractDetailId]
        ) {
          items.push({
            contractDetailId: r.contractDetailId,
            productChassisId: r.currentChassisId,
          });
        }
      });
      this.submitting = true;
      try {
        await ContractService.allocateChassis(this.contractId, { items });
        makeToastSuccess('Phân bổ thành công');
        this.$emit('allocated');
        this.hide();
      } catch (err) {
        makeToastFaile(get(err, 'response.data.message') || 'Phân bổ thất bại');
      } finally {
        this.submitting = false;
      }
    },

    formatPrice(price) {
      if (price === null || price === undefined) return '—';
      const n = Number(price);
      if (isNaN(n)) return String(price);
      return new Intl.NumberFormat('vi-VN').format(n) + ' ₫';
    },
    // Số máy placeholder ("xxxxxxxxxx") của xe ảo PO → hiển thị "Chưa có".
    engineDisplay(v) {
      const s = (v || '').trim();
      if (!s || /^x+$/i.test(s)) return 'Chưa có';
      return s;
    },
  },
};
</script>

<style lang="scss">
// ====================================================================
// AllocateChassisModal — 2 views:
//   - LIST: vehicle cards (overview, progress, action buttons).
//   - CHASSIS: chassis table for active vehicle (như reference HĐ items).
// Palette: Metronic primary + emerald/amber/indigo state colors.
// ====================================================================
$ac-bg: #f1f5f9;
$ac-surface: #ffffff;
$ac-text: #0f172a;
$ac-text-muted: #64748b;
$ac-border: #e2e8f0;
$ac-accent: #3699ff;
$ac-accent-strong: #1e7ce6;
$ac-success: #10b981;
$ac-success-soft: #d1fae5;
$ac-warning: #f59e0b;
$ac-warning-soft: #fef3c7;
$ac-picked: #6366f1;
$ac-picked-soft: #e0e7ff;
$ac-new-soft: #fef9c3;
$ac-new-text: #a16207;

#allocate-chassis-modal {
  .modal-dialog {
    max-width: 820px;
  }
  .modal-content {
    border: none;
    border-radius: 18px;
    box-shadow:
      0 24px 48px -12px rgba(15, 23, 42, 0.18),
      0 8px 16px -8px rgba(15, 23, 42, 0.08);
    overflow: hidden;
  }
  .ac-modal-body {
    padding: 0;
    background: $ac-bg;
  }

  // FontAwesome icons inherit `currentColor` từ parent — Metronic/Bootstrap
  // có global rule `i { color: ... }` override → icon ra xám dù parent đã
  // set color đúng. Ép inherit cho mọi `<i>` trong modal (đã có sẵn pattern
  // ở `.cr-status-icon` của contract-request List). Xem PIT-F-014 trong
  // `docs/pitfalls.md` + `.claude/rules/frontend-styling.md`.
  i {
    color: inherit !important;
  }
}

// ========= HEADER =========
.ac-header {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding: 22px 28px 18px;
  border-bottom: 1px solid $ac-border;

  &__title-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
  }

  &__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, $ac-accent 0%, $ac-accent-strong 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    box-shadow: 0 4px 12px rgba(54, 153, 255, 0.32);
    flex-shrink: 0;
  }

  &__title {
    flex: 1;
    min-width: 0;
    h4 {
      margin: 0 0 5px;
      font-size: 17px;
      font-weight: 700;
      color: $ac-text;
      letter-spacing: -0.01em;
      line-height: 1.3;
    }
  }

  &__product {
    color: $ac-accent;
    font-weight: 700;
  }

  &__meta {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 8px;
    font-size: 12px;
    color: $ac-text-muted;
  }
}

.ac-back-btn {
  background: $ac-accent;
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  color: #fff;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(54, 153, 255, 0.32);

  &:hover {
    background: $ac-accent-strong;
    transform: translateX(-2px);
  }
}

.ac-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 500;
  color: $ac-text-muted;
  i {
    font-size: 10px;
    opacity: 0.7;
  }
}

.ac-close-btn {
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: $ac-text-muted;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: #f1f5f9;
    color: $ac-text;
  }
}

// ========= PROGRESS =========
.ac-progress {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 16px;

  &__bar {
    flex: 1;
    height: 8px;
    background: #e2e8f0;
    border-radius: 100px;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    background: linear-gradient(90deg, $ac-warning 0%, $ac-success 100%);
    border-radius: 100px;
    transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &__label {
    display: flex;
    align-items: baseline;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    color: $ac-text-muted;
    white-space: nowrap;
  }

  &__num {
    font-size: 18px;
    font-weight: 700;
    color: $ac-text;
  }

  &__divider {
    color: #cbd5e1;
  }
}

// ========= BODY =========
.ac-body {
  padding: 18px 28px;
  max-height: 60vh;
  overflow-y: auto;
}

.ac-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: $ac-text-muted;
  gap: 14px;

  &--loading {
    flex-direction: row;
    font-size: 13px;
    padding: 40px 20px;
  }

  &--empty {
    h5 {
      margin: 0;
      color: $ac-text;
      font-weight: 600;
      font-size: 15px;
    }
    p {
      margin: 0;
      font-size: 13px;
      max-width: 380px;
    }
  }
}

.ac-empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: $ac-success-soft;
  color: $ac-success;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;

  &--warning {
    background: $ac-warning-soft;
    color: $ac-warning;
  }
}

// ========= VEHICLE LIST (VIEW 1) =========
.ac-vehicle-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ac-vehicle-card {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  gap: 16px;
  align-items: center;
  background: $ac-surface;
  border: 1px solid $ac-border;
  border-radius: 12px;
  padding: 14px 16px;
  position: relative;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  opacity: 0;
  transform: translateY(8px);
  animation: ac-card-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: calc(var(--i, 0) * 50ms + 100ms);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 14px;
    bottom: 14px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: $ac-warning;
    transition: background 0.2s ease;
  }

  &.is-picked::before {
    background: $ac-picked;
  }
  &.is-allocated::before {
    background: $ac-success;
  }

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px -2px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);
  }

  &.is-picked {
    background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
  }

  &__index {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #f1f5f9;
    color: $ac-text-muted;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
  }

  &__main {
    min-width: 0;
  }

  &__name {
    font-size: 14.5px;
    font-weight: 600;
    color: $ac-text;
    line-height: 1.3;
    margin-bottom: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__code {
    font-size: 11.5px;
    color: $ac-text-muted;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    margin-bottom: 8px;
  }

  &__status {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 10px;
    align-items: center;
  }
}

.ac-locked {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 80px;
  color: #cbd5e1;
  font-size: 14px;
}

// Card action button — primary CTA cho từng xe.
.ac-pick-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid $ac-accent;
  background: $ac-surface;
  color: $ac-accent;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  i {
    font-size: 11px;
  }

  &:hover {
    background: $ac-accent;
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(54, 153, 255, 0.32);
  }

  &.is-picked {
    background: $ac-picked-soft;
    border-color: $ac-picked;
    color: $ac-picked;
    &:hover {
      background: $ac-picked;
      color: #fff;
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.32);
    }
  }
}

// ========= STATUS PILLS =========
.ac-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  i {
    font-size: 10px;
  }

  &--success {
    background: $ac-success-soft;
    color: $ac-success;
  }
  &--pending {
    background: $ac-warning-soft;
    color: $ac-warning;
  }
  &--picked {
    background: $ac-picked-soft;
    color: $ac-picked;
  }
  &--new {
    background: $ac-new-soft;
    color: $ac-new-text;
  }
}

.ac-chassis-snapshot {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  color: $ac-text;
  font-weight: 600;
  letter-spacing: 0.02em;

  &--pending {
    color: $ac-picked;
  }

  &__label {
    font-family: inherit;
    font-size: 9.5px;
    font-weight: 700;
    color: $ac-text-muted;
    background: #f1f5f9;
    padding: 2px 5px;
    border-radius: 4px;
    letter-spacing: 0.05em;
  }

  &__sep {
    width: 4px;
    height: 4px;
    background: $ac-border;
    border-radius: 50%;
  }
}

// ========= CHASSIS TABLE (VIEW 2) =========
.ac-chassis-view {
  animation: ac-fade-in 0.25s ease;
}

.ac-chassis-table-wrap {
  background: $ac-surface;
  border-radius: 12px;
  border: 1px solid $ac-border;
  overflow: hidden;
}

.ac-chassis-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8fafc;
    th {
      padding: 12px 14px;
      font-size: 11.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: $ac-text-muted;
      text-align: left;
      border-bottom: 1px solid $ac-border;
      white-space: nowrap;
    }
  }

  tbody {
    .ac-chassis-row {
      opacity: 0;
      animation: ac-card-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      animation-delay: calc(var(--i, 0) * 30ms);
      transition: background 0.15s ease;

      &:hover {
        background: #f8fafc;
      }
      &:not(:last-child) td {
        border-bottom: 1px solid #f1f5f9;
      }

      td {
        padding: 12px 14px;
        font-size: 13px;
        color: $ac-text;
        vertical-align: middle;
      }
    }
  }

  .ac-col-action {
    width: 90px;
    text-align: right;
  }
  .ac-col-status {
    width: 80px;
  }
  .ac-col-price {
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: $ac-text-muted;
    font-size: 12.5px;
    white-space: nowrap;
  }
}

.ac-mono {
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: $ac-text;
}

.ac-choose-btn {
  padding: 6px 16px;
  border-radius: 7px;
  border: none;
  background: linear-gradient(135deg, $ac-accent 0%, $ac-accent-strong 100%);
  color: #fff;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow:
    0 2px 4px rgba(54, 153, 255, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.16);

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      0 6px 14px rgba(54, 153, 255, 0.42),
      inset 0 1px 0 rgba(255, 255, 255, 0.16);
  }
  &:active {
    transform: translateY(0);
  }
}

.ac-chassis-footer {
  padding: 10px 16px;
  border-top: 1px solid $ac-border;
  background: #f8fafc;
}

.ac-total {
  font-size: 12.5px;
  font-weight: 600;
  color: $ac-text-muted;
}

// ========= FOOTER =========
.ac-footer {
  background: $ac-surface;
  border-top: 1px solid $ac-border;
  padding: 14px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  &__hint {
    font-size: 11.5px;
    color: $ac-text-muted;
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
    i {
      font-size: 11px;
      color: $ac-accent;
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}

.ac-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  border-radius: 9px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--ghost {
    background: transparent;
    color: $ac-text-muted;
    border-color: $ac-border;
    &:hover:not(:disabled) {
      background: #f1f5f9;
      color: $ac-text;
    }
  }

  &--primary {
    background: linear-gradient(135deg, $ac-accent 0%, $ac-accent-strong 100%);
    color: #fff;
    box-shadow:
      0 2px 6px rgba(54, 153, 255, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.16);
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow:
        0 6px 16px rgba(54, 153, 255, 0.42),
        inset 0 1px 0 rgba(255, 255, 255, 0.16);
    }
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  }
}

// ========= ANIMATIONS =========
@keyframes ac-card-in {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes ac-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// ========= SCROLLBAR =========
.ac-body::-webkit-scrollbar {
  width: 8px;
}
.ac-body::-webkit-scrollbar-track {
  background: transparent;
}
.ac-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 100px;
}
.ac-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
